import Big from "big.js";
import { ZERO } from "./bigUtil.ts";
import { DisbursementType, Stage } from "./CaseConfiguration.ts";
import {
  Case,
  Claim,
  CostRecovery,
  getAwardedValue,
  Issue,
  NamedValue,
  OfferStatus,
  Party,
  Quantum,
  SettlementOffer,
} from "./CaseModel.ts";
import { estimatedOf, incurredOf } from "./costItems.ts";
import { Budget, Costs } from "./CostModel.ts";
import { getValue } from "./WeightedValue.ts";

export function getValueOfAssets({
  assets,
}: {
  assets?: NamedValue[] | "unlimited";
}): number | "unlimited" {
  if (assets === "unlimited" || assets === undefined) return "unlimited";
  if (assets.length < 1) return 0;

  let totalValue = ZERO;
  assets.forEach(
    (asset) => (totalValue = totalValue.add(getValue(asset.values))),
  );
  return totalValue.toNumber();
}

type DisbursementsByType = { [type in DisbursementType]: Big };

export type CostsByType = {
  lawFirmFees: Big;
  disbursements: DisbursementsByType;
  total: Big;
};

function add(
  augend: CostsByType,
  { lawFirmFees, disbursements, total }: CostsByType,
) {
  let type: DisbursementType;
  for (type in disbursements) {
    augend.disbursements[type] = augend.disbursements[type].add(
      disbursements[type],
    );
  }
  augend.lawFirmFees = augend.lawFirmFees.add(lawFirmFees);
  augend.total = augend.total.add(total);
}

export class CostTotals {
  private readonly incurred: CostsByType = {
    disbursements: {
      barristerFee: ZERO,
      courtFee: ZERO,
      otherDisbursement: ZERO,
      expertFee: ZERO,
    },
    lawFirmFees: ZERO,
    total: ZERO,
  };

  private readonly estimated: CostsByType = {
    disbursements: {
      barristerFee: ZERO,
      courtFee: ZERO,
      otherDisbursement: ZERO,
      expertFee: ZERO,
    },
    lawFirmFees: ZERO,
    total: ZERO,
  };

  add(other: CostTotals): CostTotals {
    add(this.incurred, other.incurred);
    add(this.estimated, other.estimated);
    return this;
  }

  addIncurredLawFirmFees(amount: Big | number) {
    this.incurred.lawFirmFees = this.incurred.lawFirmFees.add(amount);
    this.incurred.total = this.incurred.total.add(amount);
  }
  addEstimatedLawFirmFees(amount: Big | number) {
    this.estimated.lawFirmFees = this.estimated.lawFirmFees.add(amount);
    this.estimated.total = this.estimated.total.add(amount);
  }
  addIncurredDisbursement(type: DisbursementType, amount: Big | number) {
    this.incurred.disbursements[type] =
      this.incurred.disbursements[type].add(amount);
    this.incurred.total = this.incurred.total.add(amount);
  }
  addEstimatedDisbursement(type: DisbursementType, amount: Big | number) {
    this.estimated.disbursements[type] =
      this.estimated.disbursements[type].add(amount);
    this.estimated.total = this.estimated.total.add(amount);
  }

  getEstimatedCourtFees(): Big {
    return this.estimated.disbursements.courtFee;
  }

  getIncurredCourtFees(): Big {
    return this.incurred.disbursements.courtFee;
  }

  getEstimatedLawFirmFees(): Big {
    return this.estimated.lawFirmFees;
  }

  getIncurredLawFirmFees(): Big {
    return this.incurred.lawFirmFees;
  }

  getEstimated(): CostsByType {
    return { ...this.estimated };
  }

  getIncurred(): CostsByType {
    return { ...this.incurred };
  }

  getEstimatedTotal(): Big {
    return this.estimated.total;
  }
  getIncurredTotal(): Big {
    return this.incurred.total;
  }

  getAll(): CostsByType {
    const result: CostsByType = {
      disbursements: {
        barristerFee: ZERO,
        courtFee: ZERO,
        otherDisbursement: ZERO,
        expertFee: ZERO,
      },
      lawFirmFees: ZERO,
      total: ZERO,
    };
    add(result, this.incurred);
    add(result, this.estimated);
    return result;
  }
}

export function getCostsTotal({ flatFees }: Costs): CostTotals {
  const totals = new CostTotals();
  for (const item of flatFees ?? []) {
    const incurred = incurredOf(item);
    const estimated = estimatedOf(item);
    if (item.type === "lawFirmFee") {
      totals.addIncurredLawFirmFees(incurred);
      totals.addEstimatedLawFirmFees(estimated);
      continue;
    }
    totals.addIncurredDisbursement(item.type, incurred);
    totals.addEstimatedDisbursement(item.type, estimated);
  }
  return totals;
}

/**
 * A party's whole budget — every stage summed into one total, incurred and
 * estimated kept apart. The one definition behind both the analytics' party
 * stats and the "has costs" prerequisite, so the two cannot disagree.
 */
export function getBudgetTotals(budget: Budget): CostTotals {
  const total = new CostTotals();
  budget.costs.forEach((costs) => total.add(getCostsTotal(costs)));
  return total;
}

export type StageCosts = {
  stage: Stage;
  costs: CostTotals;
};

export function getCostsPerStage(budget: Budget): StageCosts[] {
  const costsPerStage = new Map<Stage, CostTotals>();
  budget.costs.forEach((costsOfStage) => {
    const { stage } = costsOfStage;
    const existing = costsPerStage.get(stage);
    if (existing) {
      existing.add(getCostsTotal(costsOfStage));
    } else {
      costsPerStage.set(stage, getCostsTotal(costsOfStage));
    }
  });
  return [...costsPerStage.entries()].map(([stage, costs]) => ({
    stage,
    costs,
  }));
}

/* ----------------------------------------------------------------------- */
/* Stage from budget — derives the last stage with incurred costs.          */
/* (Moved here from components/costs/BudgetUtils.ts so the server can use    */
/* it too; re-exported from there for existing client callers.)             */
/* ----------------------------------------------------------------------- */

export type LawyerCostsAndDisbursements = {
  lawyerCosts: number;
  disbursements: { [type in DisbursementType]: number };
  total: number;
};

export interface CostsPerStage {
  stage: Stage;
  incurred: LawyerCostsAndDisbursements;
  estimated: LawyerCostsAndDisbursements;
  sum: LawyerCostsAndDisbursements;
}

function asLawyerCostsAndDisbursements(
  costs: CostsByType,
): LawyerCostsAndDisbursements {
  const result: LawyerCostsAndDisbursements = {
    lawyerCosts: costs.lawFirmFees.toNumber(),
    disbursements: {
      barristerFee: 0,
      courtFee: 0,
      expertFee: 0,
      otherDisbursement: 0,
    },
    total: costs.total.toNumber(),
  };
  (Object.entries(costs.disbursements) as [DisbursementType, Big][]).forEach(
    ([type, amount]) => (result.disbursements[type] = amount.toNumber()),
  );
  return result;
}

export function convert({ costs }: { costs: Costs[] }): CostsPerStage[] {
  const costsPerStage = new Map<Stage, CostsPerStage>();
  costs.forEach((costs) => {
    if (costsPerStage.has(costs.stage)) return;

    const totals = getCostsTotal(costs);
    costsPerStage.set(costs.stage, {
      stage: costs.stage,
      incurred: asLawyerCostsAndDisbursements(totals.getIncurred()),
      estimated: asLawyerCostsAndDisbursements(totals.getEstimated()),
      sum: asLawyerCostsAndDisbursements(totals.getAll()),
    });
  });
  return [...costsPerStage.values()];
}

/**
 * Returns the last stage in which any costs were incurred or 0 if none were. Also returns the % of incurred costs of that stage.
 *
 * @param budget to check the progress of
 * @returns last stage with any costs incurred or 0 if no costs are incurred in budget
 */
export function getLastStageWithIncurredCosts(budget: { costs: Costs[] }): {
  stage: Stage;
  incurredPercentage: number;
} {
  const costsPerStage = convert(budget);
  // furthest (highest-index) stage with any incurred costs. We compare on
  // `stage` rather than trusting `costsPerStage` to be ordered, and avoid
  // Array.findLast (absent from the server's TS lib target).
  let lastStageWithIncurredCosts: CostsPerStage | undefined;
  for (const entry of costsPerStage) {
    if (entry.incurred.total <= 0) continue;
    if (
      lastStageWithIncurredCosts === undefined ||
      entry.stage > lastStageWithIncurredCosts.stage
    )
      lastStageWithIncurredCosts = entry;
  }
  if (!lastStageWithIncurredCosts)
    return { stage: Stage.PreAction, incurredPercentage: 0 };

  const {
    sum: { total },
    incurred,
    stage,
  } = lastStageWithIncurredCosts;

  const incurredPercentage = total > 0 ? incurred.total / total : 0;
  return { stage, incurredPercentage };
}

/* ----------------------------------------------------------------------- */
/* Amounts claimed — total maximum claimed amount from relevant claims.     */
/* (Moved here from components/CaseOverviewUtils.ts so the server can use   */
/* it too; re-exported from there for existing client callers.)            */
/* ----------------------------------------------------------------------- */

/**
 * Calculates the total maximum claimed amount from relevant claims.
 *
 * @param claims - Array of claims to process
 * @param partyIds - Array of party IDs to consider for the calculation
 * @param isByParties - Determines the calculation perspective:
 *   - `true`: Calculate amounts claimed by the specified parties (as claimants)
 *   - `false`: Calculate amounts claimed against the specified parties (as defendants)
 *
 * @returns The total maximum claimed amount across all relevant quantums
 */
function getAmountClaimed(
  claims: Claim[],
  partyIds: string[],
  isByParties: boolean,
): number {
  // a damage claimed in several claims counts once, at its highest amount
  const maximumAmountPerQuantum: { [damageId: string]: number } = {};

  for (const { claimant, defendants, quantums } of claims) {
    // any quantums in claim?
    if (!quantums || quantums.length === 0) continue;
    if (!defendants) continue;

    // is the claimant one of the given parties?
    if (isByParties && !partyIds.includes(claimant.id)) continue;

    const defendantIds = (
      isByParties
        ? // if isByParties, all defendants of the claim
          defendants
        : // if not isByParties, only the specified parties among defendants
          defendants.filter(({ id }) => partyIds.includes(id))
    ).map(({ id }) => id);

    if (defendantIds.length === 0) continue;

    // get highest liability for each quantum
    for (const quantum of quantums) {
      maximumAmountPerQuantum[quantum.damageId] = Math.max(
        maximumAmountPerQuantum[quantum.damageId] ?? 0,
        getHighestLiability(quantum, defendantIds),
      );
    }
  }
  // sum highest liabilities of all quantums
  return Object.values(maximumAmountPerQuantum).reduce(
    (previous, current) => previous + current,
    0,
  );
}

/**
 * Returns the sum of amounts claimed against a set of parties, assuming the worst case scenario:
 * - if a quantum is claimed multiple times, the highest claimed amount (against the parties) is used for calculations
 * - the highest liability across the parties is used for calculations
 */
export function getAmountClaimedAgainstParties(
  claims: Claim[],
  partyIds: string[],
): number {
  return getAmountClaimed(claims, partyIds, false);
}

/**
 * Returns the sum of amounts claimed by a set of parties, assuming the best case scenario:
 * - if a quantum is claimed multiple times, the highest claimed amount (by the parties) is used for calculations
 * - the highest liability across the defendants of the claims is used for calculations
 */
export function getAmountClaimedByParties(
  claims: Claim[],
  partyIds: string[],
): number {
  return getAmountClaimed(claims, partyIds, true);
}

/**
 * Total amount claimed in the case across ALL claimant parties (not just
 * clients). A quantum claimed in multiple claims is counted once, at the
 * highest of its claimed amounts — see {@link getAmountClaimed}, which dedupes
 * per quantum and takes the max.
 */
export function getTotalAmountClaimed(claims: Claim[]): number {
  return getAmountClaimed(
    claims,
    claims.map((claim) => claim.claimant.id),
    true,
  );
}

/**
 * Returns the highest amount any of given defendants is liable for in a quantum, given the assumed awarded value of the quantum.
 * @param quantum to check
 * @param defendantIds IDs of defendant parties to check (must be defendants of the claim of the quantum!)
 * @return highest award any of the given defendants is liable for, or 0
 */
function getHighestLiability(quantum: Quantum, defendantIds: string[]): number {
  // any liable defendants?
  if (defendantIds.length === 0) return 0;
  // get expected awarded value of quantum
  const expectedAward = getAwardedValue(quantum);
  if (expectedAward === 0) return 0;
  // are liabilities defined?
  if (quantum.liabilityShares === undefined)
    // no specific liability shares defined means everybody all defendants are 100% liable
    return expectedAward;

  // get highest liability among defendants
  let maximumLiabilityAmongDefendants = 0;
  for (const defendantId of defendantIds)
    maximumLiabilityAmongDefendants = Math.max(
      maximumLiabilityAmongDefendants,
      (quantum.liabilityShares[defendantId] ?? 0) * expectedAward,
    );

  return maximumLiabilityAmongDefendants;
}

/** The damages the claims claim, each once, at its highest amount. */
export function getQuantums(claims?: (Claim | undefined)[]): Quantum[] {
  if (!claims || claims.length === 0) return [];
  const quantums: { [damageId: string]: Quantum } = {};
  claims
    .flatMap((claim) => claim?.quantums ?? [])
    .forEach((quantum) => {
      const other = quantums[quantum.damageId];
      if (!other || other.value < quantum.value)
        quantums[quantum.damageId] = quantum;
    });
  return Object.values(quantums);
}

export function isDefendantOf(partyId: string, issue: Issue): boolean {
  return (
    issue.defendants !== undefined &&
    issue.defendants.find((defendant) => defendant.id === partyId) !== undefined
  );
}

/**
 * @param claims to search
 * @param issueId id of issue to find
 * @returns issue or undefined if no issue with given id is found
 */
export function findIssue(
  claims: Claim[] | undefined,
  issueId: string,
): Issue | undefined {
  if (claims === undefined) return undefined;
  for (let index = 0; index < claims.length; index++) {
    const claim = claims[index];
    if (claim.id === issueId) return claim;
    const child = claim.issues?.find((issue) => issue.id === issueId);
    if (child !== undefined) return child;
  }
}

export function getOpponents(
  claims: Claim[] | undefined,
  partyId: string,
): string[] {
  if (claims === undefined || claims.length === 0) return [];
  const opponents = new Set<string>();
  claims?.forEach((claim) => {
    // claimant?
    if (claim.claimant.id === partyId) {
      // all defendants of claim and its issues are opponents
      claim.defendants?.forEach((defendant) => opponents.add(defendant.id));
      claim.issues
        ?.flatMap((issue) => issue.defendants || [])
        .forEach((defendant) => opponents.add(defendant.id));
      return;
    }
    // defendant?
    if (claim.defendants?.find((d) => d.id === partyId) !== undefined) {
      opponents.add(claim.claimant.id);
      return;
    }
    claim.issues?.forEach((issue) => {
      if (issue.defendants?.find((d) => d.id === partyId)) {
        opponents.add(claim.claimant.id);
        return;
      }
    });
  });
  return [...opponents];
}

// get leaf issues/claims in which both parties are fighting
/**
 *
 * @param c case to check
 * @param partyId party
 * @param opponentId opponent of party
 * @returns all issue leafs in which party and opponent are fighting each other
 */
export function getLeafIssues(
  c: Case,
  partyId: string,
  opponentId: string,
): Issue[] {
  const leafs: Issue[] = [];
  c.claims?.forEach((claim) => {
    if (claim.claimant.id === opponentId) {
      // opponent is claimant
      // is party defendant?
      if (isDefendantOf(partyId, claim)) {
        // add claim or its issues
        if (claim.issues !== undefined && claim.issues.length > 0)
          claim.issues.forEach((issue) => leafs.push(issue));
        else leafs.push(claim);
        return;
      }
      // party is not defendant of root claim. is it defendant of any leafs?
      claim.issues?.forEach((issue) => {
        if (isDefendantOf(partyId, issue)) leafs.push(issue);
      });
      return;
    }
    // opponent is not claimant
    // is party claimant?
    if (claim.claimant.id === partyId) {
      // party is claimant
      // is opponent defendant?
      if (isDefendantOf(opponentId, claim)) {
        // add claim or its issues
        if (claim.issues !== undefined && claim.issues.length > 0)
          claim.issues.forEach((issue) => leafs.push(issue));
        else leafs.push(claim);
        return;
      }
      // party is not defendant of root claim. is it defendant of any leafs?
      claim.issues?.forEach((issue) => {
        if (isDefendantOf(opponentId, issue)) leafs.push(issue);
      });
      return;
    }
  });
  return leafs;
}

/**
 * Fix recoveries from old RecoveryType being 0 (court fees) or 1 (law firm fees) to CostType
 * @param recoveries to fix
 * @returns new recoveries if any recoveries were fixed
 */
export function fixRecoveries(
  recoveries?: CostRecovery[],
): CostRecovery[] | undefined {
  if (!recoveries || recoveries.length === 0) return;

  let fixedAnything = false;
  const newRecoveries: CostRecovery[] = recoveries.map((recovery) => {
    if ((recovery.type as unknown) === 0) {
      // old court fee type
      fixedAnything = true;
      return { ...recovery, type: "courtFee" };
    }
    if ((recovery.type as unknown) === 1) {
      // old law firm fee
      fixedAnything = true;
      return { ...recovery, type: "lawFirmFee" };
    }
    return recovery;
  });
  if (fixedAnything) return newRecoveries;
}

/** Ids of defendants on `claim` that an accepted settlement has settled. */
export function getSettledDefendantIds(
  claim: Pick<Claim, "id" | "claimant">,
  settlements: SettlementOffer[] | undefined,
): Set<string> {
  return new Set(
    (settlements ?? [])
      .filter(
        (s) =>
          s.status === OfferStatus.Accepted &&
          s.settledClaims.includes(claim.id),
      )
      .map((s) =>
        s.offereeId === claim.claimant.id ? s.offerorId : s.offereeId,
      ),
  );
}

/**
 * Whether every defendant of `claim` has an accepted settlement, counting
 * only defendants still present in `liveDefendantIds` when given (matches
 * IssueModelConverter, which drops defendants no longer among the case's
 * parties). A claim with no live defendants is vacuously settled.
 */
function isClaimFullySettled(
  claim: Pick<Claim, "id" | "claimant" | "defendants">,
  settlements: SettlementOffer[] | undefined,
  liveDefendantIds?: Set<string>,
): boolean {
  const defendants = liveDefendantIds
    ? (claim.defendants ?? []).filter((d) => liveDefendantIds.has(d.id))
    : (claim.defendants ?? []);
  if (defendants.length === 0) return true;
  const settled = getSettledDefendantIds(claim, settlements);
  return defendants.every((d) => settled.has(d.id));
}

/**
 * Whether a party is settled out of the case: every claim where it is a
 * defendant counts it among the settled defendants, and every claim where it
 * is the claimant is itself fully settled. A party absent from every claim is
 * not settled - there is nothing it could have settled out of.
 */
export function isPartySettled(
  claims: Claim[],
  parties: Party[],
  partyId: string,
  settlements: SettlementOffer[] | undefined,
): boolean {
  const claimantClaims = claims.filter(
    (claim) => claim.claimant?.id === partyId,
  );
  const defendantClaims = claims.filter((claim) =>
    (claim.defendants ?? []).some(({ id }) => id === partyId),
  );
  if (claimantClaims.length === 0 && defendantClaims.length === 0) return false;

  const liveDefendantIds = new Set(parties.map((p) => p.id));

  return (
    claimantClaims.every((claim) =>
      isClaimFullySettled(claim, settlements, liveDefendantIds),
    ) &&
    defendantClaims.every((claim) =>
      getSettledDefendantIds(claim, settlements).has(partyId),
    )
  );
}

export function computeSettlementStatus(
  claims: Claim[],
  parties: Party[],
  settlements: SettlementOffer[],
): "none" | "partial" | "full" {
  if (!settlements.some((s) => s.status === OfferStatus.Accepted))
    return "none";
  if (claims.length === 0) return "partial";

  const partyIds = new Set(parties.map((p) => p.id));
  const allClaimsFullySettled = claims.every((claim) =>
    isClaimFullySettled(claim, settlements, partyIds),
  );
  return allClaimsFullySettled ? "full" : "partial";
}

export interface SettledSummary {
  settledValue?: number;
  midpointDeviation?: number;
  exposureRemoved?: number;
  stage?: Stage;
}

/**
 * The settlement summary of a fully settled case, read off its precomputed
 * `portfolioSummary`, for the pages (bargaining, risk analysis, case value)
 * that show the agreed result instead of what would otherwise be an empty
 * page because settled claims have left the computed model. Undefined when
 * the case is not fully settled.
 */
export function getSettledSummary(c: Case): SettledSummary | undefined {
  const settlementStatus = computeSettlementStatus(
    c.claims ?? [],
    c.parties ?? [],
    c.settlements ?? [],
  );
  if (settlementStatus !== "full") return undefined;

  const realities = c.portfolioSummary?.settlementRealities;
  const acceptedStages = (c.settlements ?? [])
    .filter((offer) => offer.status === OfferStatus.Accepted)
    .map((offer) => offer.stage);
  return {
    settledValue: realities?.settledValue,
    midpointDeviation: realities?.midpointDeviation,
    exposureRemoved: realities?.exposureRemoved,
    stage: acceptedStages.length > 0 ? Math.max(...acceptedStages) : undefined,
  };
}
